import React, { useRef, useState } from 'react';
import { Plus, X, Upload, Loader, Package, Palette, Trash2, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ProductVariantsEditor - Editor de Variações (Armazenamento + Cor com álbum de imagens)
 * 
 * @param {Array} storageVariants - Lista de variantes de armazenamento
 * @param {Array} colorVariants - Lista de variantes de cor com imagens
 * @param {Function} onStorageChange - Callback quando storage muda
 * @param {Function} onColorChange - Callback quando color muda
 * @param {string} authToken - Token de autenticação para upload
 * @param {boolean} enableColorPriceAdjust - Habilita ajuste de preço por cor
 * @param {Function} onToggleColorPriceAdjust - Callback para toggle do ajuste de preço
 * @param {number} basePrice - Preço base do produto para cálculo do ajuste
 */
const ProductVariantsEditor = ({ 
  storageVariants = [], 
  colorVariants = [], 
  onStorageChange, 
  onColorChange,
  authToken,
  enableColorPriceAdjust = false,
  onToggleColorPriceAdjust,
  basePrice = 0
}) => {
  const [uploadingColorId, setUploadingColorId] = useState(null);
  const fileInputRefs = useRef({});

  // ========== STORAGE VARIANTS ==========
  
  const addStorageVariant = () => {
    const newStorage = {
      label: '',
      price: 0,
      original_price: null,
      stock: 1
    };
    onStorageChange([...storageVariants, newStorage]);
  };

  const updateStorageVariant = (index, field, value) => {
    const updated = storageVariants.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    onStorageChange(updated);
  };

  const removeStorageVariant = (index) => {
    const updated = storageVariants.filter((_, i) => i !== index);
    onStorageChange(updated);
  };

  // ========== COLOR VARIANTS ==========
  
  const addColorVariant = () => {
    // Usa timestamp + random para garantir ID único e estável
    const stableId = `color-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newColor = {
      _stableKey: stableId,  // Key estável que nunca muda
      id: '',  // ID do slug (gerado do nome)
      name: '',
      hex: '#333333',
      images: [],
      available: true,
      price_adjust: 0  // Ajuste de preço (pode ser positivo ou negativo)
    };
    onColorChange([...colorVariants, newColor]);
  };

  const updateColorVariant = (index, field, value) => {
    const updated = colorVariants.map((item, i) => {
      if (i !== index) return item;
      
      const updatedItem = { ...item, [field]: value };
      
      // Atualiza o ID slug quando o nome muda (mas não o _stableKey)
      if (field === 'name' && value) {
        updatedItem.id = value.toLowerCase()
          .replace(/\s+/g, '-')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
      }
      
      return updatedItem;
    });
    onColorChange(updated);
  };

  const removeColorVariant = (index) => {
    const updated = colorVariants.filter((_, i) => i !== index);
    onColorChange(updated);
  };

  // ========== IMAGE UPLOAD ==========
  
  const handleImageUpload = async (colorIndex, file) => {
    if (!file) return;
    
    const color = colorVariants[colorIndex];
    const uploadKey = color?._stableKey || color?.id;
    setUploadingColorId(uploadKey);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const updated = colorVariants.map((item, i) => {
          if (i !== colorIndex) return item;
          return {
            ...item,
            images: [...(item.images || []), data.url]
          };
        });
        onColorChange(updated);
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploadingColorId(null);
      if (fileInputRefs.current[colorIndex]) {
        fileInputRefs.current[colorIndex].value = '';
      }
    }
  };

  const removeImage = (colorIndex, imageIndex) => {
    const updated = colorVariants.map((item, i) => {
      if (i !== colorIndex) return item;
      return {
        ...item,
        images: item.images.filter((_, imgI) => imgI !== imageIndex)
      };
    });
    onColorChange(updated);
  };

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center gap-2 pb-2 border-b border-[#C9A24D]/30">
        <Package className="h-5 w-5 text-[#C9A24D]" />
        <div>
          <h4 className="text-[#EAEAEA] font-bold">Variações do Produto</h4>
          <p className="text-[#666] text-xs">Armazenamento define preço/estoque • Cor define álbum de imagens</p>
        </div>
      </div>

      {/* STORAGE VARIANTS */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-[#B5B5B5] flex items-center gap-2">
            <Package className="h-4 w-4 text-[#C9A24D]" />
            Variações de Armazenamento
          </label>
          <button
            type="button"
            onClick={addStorageVariant}
            className="flex items-center gap-1 text-sm text-[#C9A24D] hover:text-[#E0B85C]"
          >
            <Plus className="h-4 w-4" /> Adicionar
          </button>
        </div>

        {storageVariants.length === 0 ? (
          <p className="text-[#666] text-xs italic py-2">Nenhuma variação de armazenamento. O preço base será usado.</p>
        ) : (
          <div className="space-y-2">
            {storageVariants.map((storage, idx) => (
              <div key={idx} className="bg-[#0F0F10] rounded-lg p-3 border border-[#2A2A2D]">
                <div className="grid grid-cols-12 gap-2 items-end">
                  {/* Label */}
                  <div className="col-span-3">
                    <label className="text-[#666] text-xs">Armazenamento</label>
                    <input
                      type="text"
                      value={storage.label || ''}
                      onChange={(e) => updateStorageVariant(idx, 'label', e.target.value)}
                      placeholder="128GB"
                      className="w-full px-2 py-1.5 bg-[#1C1C1F] border border-[#2A2A2D] rounded text-[#EAEAEA] text-sm"
                    />
                  </div>
                  {/* Price */}
                  <div className="col-span-3">
                    <label className="text-[#666] text-xs">Preço PIX</label>
                    <input
                      type="number"
                      value={storage.price || ''}
                      onChange={(e) => updateStorageVariant(idx, 'price', parseFloat(e.target.value) || 0)}
                      placeholder="5999"
                      className="w-full px-2 py-1.5 bg-[#1C1C1F] border border-[#2A2A2D] rounded text-[#EAEAEA] text-sm"
                    />
                  </div>
                  {/* Original Price */}
                  <div className="col-span-3">
                    <label className="text-[#666] text-xs">Preço Original</label>
                    <input
                      type="number"
                      value={storage.original_price || ''}
                      onChange={(e) => updateStorageVariant(idx, 'original_price', parseFloat(e.target.value) || null)}
                      placeholder="(opcional)"
                      className="w-full px-2 py-1.5 bg-[#1C1C1F] border border-[#2A2A2D] rounded text-[#EAEAEA] text-sm"
                    />
                  </div>
                  {/* Stock */}
                  <div className="col-span-2">
                    <label className="text-[#666] text-xs">Estoque</label>
                    <input
                      type="number"
                      value={storage.stock || ''}
                      onChange={(e) => updateStorageVariant(idx, 'stock', parseInt(e.target.value) || 0)}
                      placeholder="10"
                      className="w-full px-2 py-1.5 bg-[#1C1C1F] border border-[#2A2A2D] rounded text-[#EAEAEA] text-sm"
                    />
                  </div>
                  {/* Remove */}
                  <div className="col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeStorageVariant(idx)}
                      className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* COLOR VARIANTS */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-[#B5B5B5] flex items-center gap-2">
            <Palette className="h-4 w-4 text-[#C9A24D]" />
            Variações de Cor (com álbum de imagens)
          </label>
          <button
            type="button"
            onClick={addColorVariant}
            className="flex items-center gap-1 text-sm text-[#C9A24D] hover:text-[#E0B85C]"
          >
            <Plus className="h-4 w-4" /> Adicionar
          </button>
        </div>

        {/* Toggle para ajuste de preço por cor */}
        {colorVariants.length > 0 && onToggleColorPriceAdjust && (
          <label className="flex items-center gap-2 text-xs bg-[#1C1C1F] p-2 rounded-lg border border-[#2A2A2D] cursor-pointer hover:border-[#3A3A3D]">
            <input
              type="checkbox"
              checked={enableColorPriceAdjust}
              onChange={(e) => onToggleColorPriceAdjust(e.target.checked)}
              className="rounded"
            />
            <DollarSign className="h-3.5 w-3.5 text-[#C9A24D]" />
            <span className="text-[#B5B5B5]">Permitir ajuste de preço por cor</span>
            {enableColorPriceAdjust && (
              <span className="text-[#666] ml-1">(soma ao preço base)</span>
            )}
          </label>
        )}

        {colorVariants.length === 0 ? (
          <p className="text-[#666] text-xs italic py-2">Nenhuma variação de cor. As imagens gerais do produto serão usadas.</p>
        ) : (
          <div className="space-y-3">
            {colorVariants.map((color, idx) => {
              // Usa _stableKey se existir, senão cria um baseado no index inicial
              const stableKey = color._stableKey || `legacy-${color.id || idx}`;
              
              return (
                <div key={stableKey} className="bg-[#0F0F10] rounded-lg p-3 border border-[#2A2A2D]">
                  {/* Color Info */}
                  <div className="flex items-center gap-3 mb-3">
                    {/* Color Picker */}
                    <input
                      type="color"
                      value={color.hex || '#333333'}
                      onChange={(e) => updateColorVariant(idx, 'hex', e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer bg-transparent border-2 border-[#2A2A2D]"
                    />
                    {/* Name */}
                    <input
                      type="text"
                      value={color.name || ''}
                      onChange={(e) => updateColorVariant(idx, 'name', e.target.value)}
                      placeholder="Nome da cor (ex: Azul Titânio)"
                      className="flex-1 px-3 py-2 bg-[#1C1C1F] border border-[#2A2A2D] rounded text-[#EAEAEA] text-sm"
                    />
                    {/* Available Toggle */}
                    <label className="flex items-center gap-2 text-xs whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={color.available !== false}
                        onChange={(e) => updateColorVariant(idx, 'available', e.target.checked)}
                        className="rounded"
                      />
                      <span className={color.available !== false ? 'text-green-400' : 'text-red-400'}>
                        {color.available !== false ? 'Disponível' : 'Indisponível'}
                      </span>
                    </label>
                    {/* Remove */}
                    <button
                      type="button"
                      onClick={() => removeColorVariant(idx)}
                      className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Price Adjustment (only if enabled) */}
                  {enableColorPriceAdjust && (
                    <div className="mb-3 flex items-center gap-3">
                      <div className="flex-1">
                        <label className="text-[#666] text-xs mb-1 block">
                          Ajuste de preço (+ ou -)
                        </label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#666] text-sm">R$</span>
                          <input
                            type="number"
                            value={color.price_adjust || ''}
                            onChange={(e) => updateColorVariant(idx, 'price_adjust', parseFloat(e.target.value) || 0)}
                            placeholder="0"
                            step="0.01"
                            className="w-full pl-8 pr-3 py-1.5 bg-[#1C1C1F] border border-[#2A2A2D] rounded text-[#EAEAEA] text-sm"
                          />
                        </div>
                      </div>
                      {color.price_adjust !== 0 && color.price_adjust && (
                        <div className="text-xs text-[#666] pt-4">
                          Preço final: <span className="text-[#C9A24D] font-medium">
                            R$ {((basePrice || 0) + (color.price_adjust || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Images Album */}
                  <div>
                    <label className="text-[#666] text-xs mb-2 block">
                      Álbum de Imagens ({(color.images || []).length} fotos)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {/* Existing Images */}
                      {(color.images || []).map((img, imgIdx) => (
                        <div key={imgIdx} className="relative group">
                          <img 
                            src={img} 
                            alt="" 
                            className="w-16 h-16 object-cover rounded border border-[#2A2A2D]"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(idx, imgIdx)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                          <span className="absolute bottom-0 left-0 bg-black/70 text-white text-[9px] px-1 rounded-tr">
                            {imgIdx + 1}
                          </span>
                        </div>
                      ))}

                      {/* Upload Button */}
                      <label className="w-16 h-16 border-2 border-dashed border-[#2A2A2D] rounded flex flex-col items-center justify-center cursor-pointer hover:border-[#C9A24D] transition-colors">
                        <input
                          type="file"
                          ref={el => fileInputRefs.current[idx] = el}
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleImageUpload(idx, e.target.files?.[0])}
                        />
                        {uploadingColorId === (color._stableKey || color.id) ? (
                          <Loader className="h-5 w-5 text-[#C9A24D] animate-spin" />
                        ) : (
                          <>
                            <Upload className="h-5 w-5 text-[#666]" />
                            <span className="text-[9px] text-[#666] mt-0.5">Adicionar</span>
                          </>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info */}
      {storageVariants.length > 0 && (
        <div className="bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-lg p-3">
          <p className="text-[#C9A24D] text-xs">
            <strong>Preço no card:</strong> "A partir de R$ {Math.min(...storageVariants.map(s => s.price || Infinity)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}"
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductVariantsEditor;
